// src/pages/ResetPassword.jsx
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

export default function ResetPassword() {
  const { currentUser, logout } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [focused, setFocused] = useState("");

  async function handleReset() {
    if (!newPassword || !confirmPassword) return setError("Please fill all fields");
    if (newPassword.length < 6) return setError("Password must be at least 6 characters");
    if (newPassword !== confirmPassword) return setError("Passwords do not match");

    try {
      setLoading(true);
      setError("");

      // Call admin reset endpoint
      console.log("Calling admin-reset-password for user:", currentUser?.uid);
      await api.post("/auth/admin-reset-password", {
        userId: currentUser.uid,
        newPassword,
      });
      
      // Also update profile
      await api.put(`/faculty/${currentUser.uid}/profile`, {
        mustResetPassword: false,
        password: newPassword,
      });

      setSuccess(true);

      // Sign out and redirect to production login page after short delay
      setTimeout(async () => {
        await logout();
        window.location.href = "https://portfolio.apdch.edu.in/";
      }, 2500);
    } catch (err) {
      console.error("Password reset error:", err);
      const errorMsg = err.message || "Failed to reset password. Try again.";
      
      if (errorMsg.includes("Access denied")) {
        setError("⛔ You don't have permission to reset passwords. Contact your administrator.");
      } else if (errorMsg.includes("No token")) {
        setError("🔓 Your session has expired. Please log out and log in again.");
      } else if (errorMsg.includes("Invalid token")) {
        setError("🔓 Invalid session. Please log out and log in again.");
      } else {
        setError("❌ " + errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 50%, #f5f0ff 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Nunito', sans-serif" }}>

      {/* Decorative bg shapes */}
      <div style={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)", top: -200, right: -100, pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.05) 0%, transparent 70%)", bottom: -100, left: -100, pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: 480, padding: "0 20px" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", background: "white", borderRadius: 12, padding: "10px 20px 10px 10px", boxShadow: "0 4px 20px rgba(37,99,235,0.12)", gap: 12 }}>
            <img src="/apdch_logo.png" alt="APDCH Logo" style={{ width: 72, height: 72, objectFit: "contain" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "left" }}>
              <span style={{ fontWeight: 900, fontSize: 17, color: "#7b1a1a", letterSpacing: 0.3, lineHeight: 1.3 }}>
                Adhiparasakthi Dental College &amp; Hospital
              </span>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#7b1a1a", letterSpacing: 2, opacity: 0.75 }}>
                MELMARUVATHUR
              </span>
            </div>
          </div>
        </div>

        <div style={{ background: "#fff", borderRadius: 24, padding: "40px 36px", boxShadow: "0 8px 40px rgba(15,37,87,0.1)", border: "1px solid rgba(37,99,235,0.08)" }}>

          {/* Security badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 20, padding: "4px 14px", marginBottom: 24 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#f59e0b" }} />
            <span style={{ color: "#b45309", fontSize: 14, fontWeight: 700, letterSpacing: 1 }}>PASSWORD RESET REQUIRED</span>
          </div>

          <h2 style={{ color: "#0f2557", fontSize: 26, fontWeight: 800, marginBottom: 6 }}>🔐 Set New Password</h2>
          <p style={{ color: "#64748b", fontSize: 15, marginBottom: 28, lineHeight: 1.5 }}>
            Welcome! Since this is your first login, please set a new password to secure your account.
          </p>

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", color: "#dc2626", fontSize: 15, marginBottom: 18, fontWeight: 600 }}>
              {error}
            </div>
          )}

          {success ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
              <h3 style={{ color: "#16a34a", fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Password Changed Successfully!</h3>
              <p style={{ color: "#64748b", fontSize: 16, marginBottom: 8 }}>Your password has been updated.</p>
              <p style={{ color: "#94a3b8", fontSize: 14 }}>Redirecting to login page...</p>
              <div style={{ marginTop: 20 }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: "#e2e8f0", margin: "0 auto", overflow: "hidden" }}>
                  <div style={{ width: "100%", height: "100%", background: "linear-gradient(90deg, #16a34a, #22c55e)", animation: "loading 2.5s linear forwards" }} />
                </div>
              </div>
              <style>{`@keyframes loading { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
            </div>
          ) : (
            <>
              {/* New Password */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: "#374151", fontSize: 15, fontWeight: 700, display: "block", marginBottom: 8 }}>New Password</label>
                <div style={{ display: "flex", alignItems: "center", background: focused === "new" ? "#eaf1ff" : "#f8fafc", border: `1.5px solid ${focused === "new" ? "#2563eb" : "#e2e8f0"}`, borderRadius: 12, padding: "0 16px", transition: "all 0.2s" }}>
                  <span style={{ fontSize: 19, marginRight: 10 }}>🔑</span>
                  <input
                    type={showPass ? "text" : "password"} placeholder="Enter new password (min 6 chars)" value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    onFocus={() => setFocused("new")} onBlur={() => setFocused("")}
                    style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#1e293b", fontSize: 17, padding: "13px 0", fontFamily: "'Nunito', sans-serif" }}
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div style={{ marginBottom: 10 }}>
                <label style={{ color: "#374151", fontSize: 15, fontWeight: 700, display: "block", marginBottom: 8 }}>Confirm Password</label>
                <div style={{ display: "flex", alignItems: "center", background: focused === "confirm" ? "#eaf1ff" : "#f8fafc", border: `1.5px solid ${focused === "confirm" ? "#2563eb" : "#e2e8f0"}`, borderRadius: 12, padding: "0 16px", transition: "all 0.2s" }}>
                  <span style={{ fontSize: 19, marginRight: 10 }}>🔒</span>
                  <input
                    type={showPass ? "text" : "password"} placeholder="Re-enter new password" value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    onFocus={() => setFocused("confirm")} onBlur={() => setFocused("")}
                    onKeyDown={e => e.key === "Enter" && handleReset()}
                    style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#1e293b", fontSize: 17, padding: "13px 0", fontFamily: "'Nunito', sans-serif" }}
                  />
                  <span onClick={() => setShowPass(v => !v)} style={{ cursor: "pointer", fontSize: 18 }}>{showPass ? "🙈" : "👁️"}</span>
                </div>
              </div>

              {/* Password strength indicator */}
              {newPassword && (
                <div style={{ marginBottom: 20, marginTop: 12 }}>
                  <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
                    {[1, 2, 3, 4].map(level => (
                      <div key={level} style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: newPassword.length >= level * 3
                          ? newPassword.length >= 12 ? "#16a34a" : newPassword.length >= 8 ? "#f59e0b" : "#ef4444"
                          : "#e2e8f0",
                        transition: "all 0.3s"
                      }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 13, color: newPassword.length >= 12 ? "#16a34a" : newPassword.length >= 8 ? "#f59e0b" : "#ef4444", fontWeight: 600 }}>
                    {newPassword.length >= 12 ? "✅ Strong password" : newPassword.length >= 8 ? "⚡ Good password" : newPassword.length >= 6 ? "⚠️ Fair password" : "❌ Too short (min 6 chars)"}
                  </div>
                </div>
              )}

              {/* Match indicator */}
              {confirmPassword && (
                <div style={{ marginBottom: 20, fontSize: 14, fontWeight: 600, color: newPassword === confirmPassword ? "#16a34a" : "#dc2626" }}>
                  {newPassword === confirmPassword ? "✅ Passwords match" : "❌ Passwords do not match"}
                </div>
              )}

              <button
                onClick={handleReset} disabled={loading}
                style={{ width: "100%", background: loading ? "#93c5fd" : "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)", border: "none", borderRadius: 12, padding: "14px", color: "#fff", fontSize: 18, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 4px 16px rgba(37,99,235,0.3)", fontFamily: "'Nunito', sans-serif", marginTop: 8 }}
              >
                {loading ? "⏳ Updating Password..." : "🔐 Set New Password"}
              </button>
            </>
          )}
        </div>

        <p style={{ color: "#94a3b8", fontSize: 14, textAlign: "center", marginTop: 20 }}>
          Contact admin if you face any issues
        </p>
      </div>
    </div>
  );
}
